/**
 * Widget Skeleton
 *
 * Animated loading placeholder matching the widget layout.
 * Used as Suspense fallback and initial loading state.
 */

interface WidgetSkeletonProps {
  /** Height variant to match the widget being loaded */
  variant?: 'chart' | 'list' | 'stats' | 'gauge' | 'bars' | 'waterfall' | 'default';
}

function SkeletonBar({ className = '' }: { className?: string }) {
  return (
    <div
      className={`bg-bg-hover rounded-sm animate-pulse ${className}`}
    />
  );
}

export function WidgetSkeleton({ variant = 'default' }: WidgetSkeletonProps) {
  return (
    <div className="widget">
      {/* Header skeleton */}
      <div className="widget-header">
        <div className="flex items-center gap-2">
          <SkeletonBar className="w-3.5 h-3.5 rounded-xs" />
          <SkeletonBar className="w-24 h-3" />
        </div>
        <SkeletonBar className="w-16 h-3" />
      </div>

      {/* Body skeleton — variant-specific */}
      <div className="widget-body">
        {variant === 'chart' && <ChartSkeleton />}
        {variant === 'list' && <ListSkeleton />}
        {variant === 'stats' && <StatsSkeleton />}
        {variant === 'gauge' && <GaugeSkeleton />}
        {variant === 'bars' && <BarsSkeleton />}
        {variant === 'waterfall' && <WaterfallSkeleton />}
        {variant === 'default' && <DefaultSkeleton />}
      </div>
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="h-32 flex items-end gap-1 px-2">
      {[40, 65, 45, 80, 55, 70, 35, 60, 75, 50, 85, 42].map((h, i) => (
        <div
          key={i}
          className="flex-1 bg-bg-hover rounded-t animate-pulse"
          style={{ height: `${h}%`, animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-2">
          <SkeletonBar className="w-12 h-3" />
          <SkeletonBar className="flex-1 h-3" />
        </div>
      ))}
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-bg-primary rounded-lg p-2.5">
            <SkeletonBar className="w-10 h-5 mb-1" />
            <SkeletonBar className="w-16 h-2.5" />
          </div>
        ))}
      </div>
      <div className="space-y-1">
        <SkeletonBar className="w-full h-1.5 rounded-full" />
      </div>
    </div>
  );
}

function GaugeSkeleton() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-bg-primary rounded-lg p-2 text-center">
            <SkeletonBar className="w-8 h-4 mx-auto mb-1" />
            <SkeletonBar className="w-10 h-2.5 mx-auto" />
          </div>
        ))}
      </div>
      <div className="space-y-1.5">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <SkeletonBar className="w-1.5 h-1.5 rounded-full" />
              <SkeletonBar className="w-16 h-2.5" />
            </div>
            <SkeletonBar className="w-12 h-2.5" />
          </div>
        ))}
      </div>
    </div>
  );
}

function BarsSkeleton() {
  return (
    <div className="space-y-2">
      {[85, 60, 45, 30, 20].map((w, i) => (
        <div key={i} className="space-y-1">
          <div className="flex items-center justify-between">
            <SkeletonBar className="w-20 h-2.5" />
            <SkeletonBar className="w-10 h-2.5" />
          </div>
          <div className="h-1.5 bg-bg-primary rounded-full overflow-hidden">
            <div
              className="h-full bg-bg-hover rounded-full animate-pulse"
              style={{ width: `${w}%`, animationDelay: `${i * 100}ms` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function WaterfallSkeleton() {
  const spans = [
    { offset: 0, width: 95 },
    { offset: 5, width: 18 },
    { offset: 25, width: 15 },
    { offset: 42, width: 10 },
    { offset: 54, width: 5 },
    { offset: 60, width: 30 },
    { offset: 92, width: 6 },
  ];

  return (
    <div className="space-y-2">
      {spans.map((span, i) => (
        <div key={i} className="flex items-center gap-2">
          <SkeletonBar className="w-28 h-3 shrink-0" />
          <div className="flex-1 h-5 relative bg-bg-primary rounded-sm overflow-hidden">
            <div
              className="absolute top-1 bottom-1 bg-bg-hover rounded-sm animate-pulse"
              style={{
                left: `${span.offset}%`,
                width: `${span.width}%`,
                animationDelay: `${i * 120}ms`,
              }}
            />
          </div>
          <SkeletonBar className="w-10 h-3 shrink-0" />
        </div>
      ))}
    </div>
  );
}

function DefaultSkeleton() {
  return (
    <div className="space-y-3">
      <SkeletonBar className="w-3/4 h-3" />
      <SkeletonBar className="w-1/2 h-3" />
      <SkeletonBar className="w-5/6 h-3" />
    </div>
  );
}
